import { useCallback } from "react";
import { atom, useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";

import { getUtilityBills } from "api";

export interface BillEntriesNavigationFetchContext {
  query: Record<string, unknown>;
  page: number;
  size: number;
  pageOffset: number;
}

export interface BillEntriesNavigationState {
  ids: string[];
  totalCount: number;
  fetchContext?: BillEntriesNavigationFetchContext | null;
}

const billEntriesNavigationState = atom<BillEntriesNavigationState | null>({
  key: "billEntriesNavigationState",
  default: null,
});

export const useBillEntriesNavigation = () =>
  useRecoilValue(billEntriesNavigationState);

export const useSetBillEntriesNavigation = () => {
  const set = useSetRecoilState(billEntriesNavigationState);
  return useCallback(
    (state: BillEntriesNavigationState | null) => set(state),
    [set],
  );
};

export const useFetchNextBillEntriesPage = () =>
  useRecoilCallback(({ snapshot, set }) => async (): Promise<void> => {
    const state = await snapshot.getPromise(billEntriesNavigationState);
    const ctx = state?.fetchContext;
    if (!state || !ctx || state.ids.length >= state.totalCount) return;

    const nextPage = ctx.page + 1;
    const response = await getUtilityBills({ ...(ctx.query as any), page: nextPage, size: ctx.size });
    const newIds = (response.bills ?? []).map((b) => String(b.id));

    set(billEntriesNavigationState, {
      ids: [...state.ids, ...newIds],
      totalCount: response.count,
      fetchContext: { ...ctx, page: nextPage },
    });
  }, []);

export const useFetchPrevBillEntriesPage = () =>
  useRecoilCallback(({ snapshot, set }) => async (): Promise<void> => {
    const state = await snapshot.getPromise(billEntriesNavigationState);
    const ctx = state?.fetchContext;
    if (!state || !ctx || ctx.pageOffset === 0) return;

    const prevPageIndex = Math.floor(ctx.pageOffset / ctx.size) - 1;
    const response = await getUtilityBills({ ...(ctx.query as any), page: prevPageIndex, size: ctx.size });
    const newIds = (response.bills ?? []).map((b) => String(b.id));

    set(billEntriesNavigationState, {
      ids: [...newIds, ...state.ids],
      totalCount: response.count,
      fetchContext: { ...ctx, pageOffset: ctx.pageOffset - newIds.length },
    });
  }, []);
